import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@fluentui/react-components";
import "./CSS/SlaWidget.css";

const SlaWidget = ({ totalSla, queueKpis }) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(0);
  const [truncatedLabels, setTruncatedLabels] = useState({});
  const [itemsPerPage, setItemsPerPage] = useState(8);
  const labelRefs = useRef({});
  const containerRef = useRef(null);

  // Calculer dynamiquement le nombre d'items affichables selon la largeur
  useEffect(() => {
    const calculateItemsPerPage = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        
        // Calculer selon la largeur (largeur minimale par barre : 60px pour petits écrans, 70px pour grands)
        const minBarWidth = containerWidth < 600 ? 60 : 70;
        const availableWidth = containerWidth - 40; // padding
        const itemsByWidth = Math.floor(availableWidth / minBarWidth);
        
        const newItemsPerPage = Math.max(3, Math.min(itemsByWidth, 30)); // Min 3, Max 30
        
        if (newItemsPerPage !== itemsPerPage) {
          setItemsPerPage(newItemsPerPage);
          setCurrentPage(0); // Réinitialiser à la première page
        }
      }
    };

    // Calculer immédiatement
    const timer = setTimeout(calculateItemsPerPage, 100);

    // Observer pour détecter les changements de taille
    const resizeObserver = new ResizeObserver(calculateItemsPerPage);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      clearTimeout(timer);
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
      }
    };
  }, [itemsPerPage]);

  // Fonction pour déterminer la couleur selon le SLA
  const getSlaColorClass = (slaValue) => {
    if (slaValue >= 90) return 'sla-green-dark';  // Excellent
    if (slaValue >= 80) return 'sla-green-light'; // Très bon
    if (slaValue >= 70) return 'sla-lime';        // Bon
    if (slaValue >= 60) return 'sla-orange';      // Moyen
    if (slaValue >= 50) return 'sla-red-light';   // Faible
    return 'sla-red-dark';                         // Très faible
  };

  // Préparer les données pour le graphique
  const chartData = [];
  
  // Ajouter le SLA total en premier
  if (totalSla != null) {
    chartData.push({
      label: "Total",
      value: parseFloat(totalSla) || 0,
      isTotal: true
    });
  }

  // Ajouter le SLA de chaque queue
  if (queueKpis && Object.keys(queueKpis).length > 0) {
    Object.values(queueKpis).forEach(kpi => {
      if (kpi && kpi.queueName) {
        chartData.push({
          label: kpi.queueName,
          value: parseFloat(kpi.sla) || 0,
          isTotal: false
        });
      }
    });
  }

  // Pagination
  const totalPages = Math.ceil(chartData.length / itemsPerPage);
  const startIndex = currentPage * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = chartData.slice(startIndex, endIndex);

  // Trouver la valeur maximale pour l'échelle
  const maxValue = Math.max(100, ...chartData.map(item => item.value));

  const handlePrevious = () => {
    setCurrentPage(prev => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentPage(prev => Math.min(totalPages - 1, prev + 1));
  };

  // Vérifier quels labels sont tronqués
  useEffect(() => {
    const checkTruncation = () => {
      const newTruncatedLabels = {};
      Object.entries(labelRefs.current).forEach(([key, ref]) => {
        if (ref) {
          newTruncatedLabels[key] = ref.scrollWidth > ref.clientWidth;
        }
      });
      setTruncatedLabels(newTruncatedLabels);
    };
    
    // Vérifier après le rendu
    const timer = setTimeout(checkTruncation, 100);
    return () => clearTimeout(timer);
  }, [currentData]);

  return (
    <div className="sla-widget" ref={containerRef}>
      <div className="sla-widget-header kpi-card-header">
        <span className="drag-handle">⋮⋮</span>
        <h3>{t("REALTIME.SLA")}</h3>
        <div className="sla-widget-nav">
          {totalPages > 1 && (
            <>
              <button 
                className="sla-nav-btn" 
                onClick={handlePrevious} 
                disabled={currentPage === 0}
              >
                ←
              </button>
              <span className="sla-page-indicator">
                {currentPage + 1} / {totalPages}
              </span>
              <button 
                className="sla-nav-btn" 
                onClick={handleNext} 
                disabled={currentPage === totalPages - 1}
              >
                →
              </button>
            </>
          )}
        </div>
      </div>
       {chartData?.length === 0 ? (
        <div className="no-data">{t("REALTIME.NO_QUEUE_DATA")}</div>
      ) :  <div className="sla-chart">
       
        {currentData.map((item, index) => {
          const heightPercentage = (item.value / maxValue) * 100;
          const labelKey = `${startIndex + index}`;
          const isTruncated = truncatedLabels[labelKey];
          
          const labelElement = (
            <div 
              ref={el => labelRefs.current[labelKey] = el}
              className="sla-bar-label"
              style={{ cursor: isTruncated ? 'pointer' : 'default' }}
            >
              {item.label}
            </div>
          );

          return (
            <div key={labelKey} className="sla-bar-container">
              <div className="sla-bar-wrapper">
                <div className="sla-bar-track">
                  <div className="sla-bar-value">{item.value.toFixed(0)}%</div>
                  <div 
                    className={`sla-bar-fill ${item.isTotal ? 'total' : getSlaColorClass(item.value)}`}
                    style={{ height: `${heightPercentage}%` }}
                  />
                </div>
              </div>
              {isTruncated ? (
                <Tooltip content={item.label} relationship="label">
                  {labelElement}
                </Tooltip>
              ) : (
                labelElement
              )}
            </div>
          );
        })}
      </div>}
     
    </div>
  );
};

export default SlaWidget;
